import './savedSearchHistoryComponent.scss';
import React, {Fragment, MouseEventHandler, useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {SavedSearchWithId} from '../duck/saveSearchComponent-types';
import {useSearchPageType} from '../../search-hooks';
import {
    deleteSavedSearchFolder,
    deleteSavedSearchItem,
    shareSavedSearchItem
} from '../duck/saveSearchComponent-actions';
import {HibuSearchRequest, SortType} from '../../search-types';
import {searchSavedSearch, setQueryFieldsAction, sortChangeAction} from '../../duck/search-actions';
import {Dropdown} from 'react-bootstrap';
import {SvgIcon} from '../../../icons/svgIcon';
import {Icons, IconType} from '../../../icons/icons';
import {useStateContainerName} from '../../../settings/duck/settings-hooks';
import classNames from 'clsx';
import {doFetchFacets} from '../../../services/search-service';
import {VerticalSeparator} from '../../../common/verticalSeparator/verticalSeparator';
import {HibuTooltip} from '../../../common/hibuTooltip/hibuTooltip';
import {useWithConfirmation} from '../../../common/hibuModal/hibuModal';
import {QUERY_FIELDS_SEPARATOR} from '../../const';
import {useSettingsConfig} from '../../../config/config-hooks';
import {isShowFieldSpecificSearchEnabled} from '../../../utils/utils';
import {changeFilterPersistence, clearAllSelectedFilters, expandFilterGroup} from '../../../facets/duck/filter.actions';

type SavedSearchHistoryProps = {
    savedSearches?: SavedSearchWithId[];
    savedSearchesSharedByOthers?: SavedSearchWithId[];
    savedSearchesSharedByOthersEnabled: boolean;
}

type SavedSearchGroupProps = {
    uniqueName: string;
    savedSearches?: SavedSearchWithId[];
    icon: IconType;
    editionPermission: boolean;
    mayShare: boolean;
    titleKey: string;
    emptyContentKey: string;
}

export const SavedSearchHistory = React.memo(({
                                                  savedSearches,
                                                  savedSearchesSharedByOthers,
                                                  savedSearchesSharedByOthersEnabled
                                              }: SavedSearchHistoryProps) => {
    return <>
        <VerticalSeparator/>
        <SavedSearchGroup uniqueName={'own'} icon={Icons.SEARCH_FOLDER}
                          editionPermission={true}
                          mayShare={savedSearchesSharedByOthersEnabled}
                          savedSearches={savedSearches}
                          titleKey={'search.saveSearch.action.ownSearchTitle'}
                          emptyContentKey={'search.saveSearch.action.ownEmpty'}/>
        {savedSearchesSharedByOthersEnabled && (
            <SavedSearchGroup uniqueName={'shared'} icon={Icons.SHARED_FOLDER}
                              editionPermission={false}
                              mayShare={false}
                              savedSearches={savedSearchesSharedByOthers}
                              titleKey={'search.saveSearch.action.otherSearchTitle'}
                              emptyContentKey={'search.saveSearch.action.sharedEmpty'}/>
        )}
    </>;
});

const SavedSearchGroup = React.memo(({
                                         uniqueName,
                                         savedSearches,
                                         editionPermission = false,
                                         mayShare = false,
                                         icon,
                                         titleKey,
                                         emptyContentKey
                                     }: SavedSearchGroupProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const searchPageType = useSearchPageType();
    const containerName = useStateContainerName();
    const [selectedFolder, setSelectedFolder] = useState<string | undefined>(undefined);
    const navigate = useNavigate();
    const settingsConfig = useSettingsConfig();

    const isOnTheContainer = useCallback((currentValue: SavedSearchWithId) => {
        return currentValue.request?.container === containerName;
    }, [containerName]);

    const allFolders = useCallback(() => {
        return savedSearches?.reduce((currentList: string[], currentValue: SavedSearchWithId) => {
            if (currentList.includes(currentValue.folder) || !isOnTheContainer(currentValue)) {
                return currentList;
            }
            return [...currentList, currentValue.folder];
        }, []) || [];
    }, [savedSearches, isOnTheContainer]);

    function onSelectSavedSearch(request: HibuSearchRequest, retainFilters: boolean) {
        const currentSortType: SortType = {
            field: request.sort.field as string,
            order: request.sort.order as string
        };
        // Restore the retain-filters mode stored with this saved search so both the URL (rf) and the
        // checkbox reflect it; changeUrl() inside searchSavedSearch() then serialises the updated state.
        dispatch(changeFilterPersistence(retainFilters));
        if (!retainFilters) {
            dispatch(clearAllSelectedFilters());
        }
        dispatch(sortChangeAction(searchPageType, currentSortType));
        for (const fieldName in request.filters) {
            dispatch(expandFilterGroup(fieldName));
        }
        if (isShowFieldSpecificSearchEnabled(settingsConfig)) {
            dispatch(setQueryFieldsAction(request.queryFields ? request.queryFields.join(QUERY_FIELDS_SEPARATOR) : null));
        }
        dispatch(doFetchFacets(searchPageType, request));
        dispatch(searchSavedSearch(request, navigate));
    }

    function onDeleteSavedSearch(savedSearchId: number) {
        dispatch(deleteSavedSearchItem(searchPageType, savedSearchId));
    }

    function onShareSavedSearch(savedSearchId: number, shared: boolean) {
        dispatch(shareSavedSearchItem(searchPageType, savedSearchId, shared));
    }

    function onDeleteSavedSearchFolder(folderName: string) {
        dispatch(deleteSavedSearchFolder(searchPageType, folderName));
    }

    function isOnTheSelectedFolder(item: SavedSearchWithId) {
        return item.folder === selectedFolder;
    }

    const folderListComponent = (): React.JSX.Element => {
        return (<div className="saved-search-menu-content">{allFolders().length > 0 ?
            allFolders().map((folder, index) =>
                (
                    <Fragment key={`${folder}-${index}`}>
                        <SavedSearchFolderItem name={folder} editable={editionPermission}
                                               onClick={() => {
                                                   setSelectedFolder(folder);
                                               }}
                                               onDelete={() => onDeleteSavedSearchFolder(folder)}
                        />
                    </Fragment>)
            )
            :
            <div className={'saved-search-menu-content-empty'}>
                {t(emptyContentKey)}
            </div>
        }
        </div>);
    };

    const savedSearchesItemList = (): React.JSX.Element => {
        return (<div className="saved-search-menu-content">
            {(savedSearches || [])
                .filter(item => isOnTheContainer(item) && isOnTheSelectedFolder(item))
                .map(item => (
                    <Fragment key={item.id}>
                        <SaveSearchItem
                            savedQuery={item}
                            onSelect={onSelectSavedSearch}
                            onDelete={onDeleteSavedSearch}
                            onShared={onShareSavedSearch}
                            editable={editionPermission}
                            mayShare={mayShare}
                        />
                    </Fragment>
                ))}
        </div>);
    };

    const contentHeader = (): React.JSX.Element => {
        return <Dropdown.Header>
            {selectedFolder &&
                <SvgIcon icon={Icons.ARROW_BACK} id={`icon-search-folder-${uniqueName}`} className={'icon'}
                         onClick={() => setSelectedFolder(undefined)}/>}
            {selectedFolder || t(titleKey)}</Dropdown.Header>;
    };

    return (<div id={`saved-search-history-${uniqueName}`} className={`saved-search-history`}>
            <Dropdown id={`saved-search-dropdown-${uniqueName}`}
                      onToggle={(isOpen) => !isOpen && setSelectedFolder(undefined)}>
                <Dropdown.Toggle id={`saved-search-toggle-${uniqueName}`}>
                    <SvgIcon icon={icon} id={`icon-search-folder-${uniqueName}`}/>
                </Dropdown.Toggle>
                <Dropdown.Menu id={`saved-search-menu-${uniqueName}`} show={false}>
                    {contentHeader()}
                    {selectedFolder ? savedSearchesItemList() : folderListComponent()}
                </Dropdown.Menu>
            </Dropdown>
        </div>
    );
});

type SavedSearchFolderProps = {
    name: string;
    onClick: MouseEventHandler<HTMLDivElement>;
    editable?: boolean
    onDelete?: MouseEventHandler<HTMLDivElement>;
}

const SavedSearchFolderItem = React.memo(({
                                              name,
                                              onClick,
                                              editable = false,
                                              onDelete
                                          }: SavedSearchFolderProps) => {
        const {t} = useTranslation();
    const {onClick: onDeleteClick} = useWithConfirmation(
        t('search.saveSearch.modal.deleteSavedSearch.folder.message', {name}),
        {
            backdrop: true,
            keyboard: true,
            closeButton: false,
            title: t('search.saveSearch.modal.deleteSavedSearch.folder.title'),
            cancel: t('general.cancel'),
            ok: t('general.clear'),
        });

        return (
            <div className={'saved-search-item'}>
                <div className={classNames('saved-search-item-info-wrapper')}>
                    <HibuTooltip tooltipKey={name} placement={'auto'} key={name}>
                        <div className="saved-search-item-link link" onClick={onClick}>{name}</div>
                    </HibuTooltip>
                    {editable && <SvgIcon
                        className="saved-search-icon"
                        icon={Icons.DELETE}
                        onClick={onDeleteClick(onDelete)}
                        alt={t('search.saveSearch.remove')}
                    />}
                </div>
            </div>
        );
    }
);

interface SaveSearchItemProps {
    savedQuery: SavedSearchWithId;
    onSelect: (request: HibuSearchRequest, retainFilters: boolean) => void;
    onShared?: (id: number, shared: boolean) => void;
    onDelete?: (id: number) => void;
    editable: boolean;
    mayShare: boolean;
}

export const SaveSearchItem = React.memo(({
                                              savedQuery,
                                              onSelect,
                                              onDelete,
                                              onShared,
                                              editable,
                                              mayShare
                                          }: SaveSearchItemProps) => {
    const {t} = useTranslation();
    const {onClick: onDeleteClick} = useWithConfirmation(
        t('search.saveSearch.modal.deleteSavedSearch.single.message', {name: savedQuery.name}),
        {
            backdrop: true,
            keyboard: true,
            closeButton: false,
            title: t('search.saveSearch.modal.deleteSavedSearch.single.title'),
            cancel: t('general.cancel'),
            ok: t('general.clear'),
        });

    const handleShare = () => {
        if (onShared) {
            onShared(savedQuery.id, !savedQuery.shared);
        }
    };

    const handleDelete = () => {
        if (onDelete) {
            onDelete(savedQuery.id);
        }
    };

    const getIconTitle = () => {
        return !savedQuery.shared ? t('search.saveSearch.action.shared') : t('search.saveSearch.action.notShared');
    };

    return (
        <Dropdown.Item className={'saved-search-item'}>
            <div className={classNames('saved-search-item-info-wrapper')}>
                <HibuTooltip tooltipKey={savedQuery.name} placement={'auto'} key={savedQuery.name}>
                    <div className="saved-search-item-info-text"
                         onClick={() => onSelect(savedQuery.request, savedQuery.retainFilters ?? true)}>
                        <span className={'saved-search-item-link link'}>{savedQuery.name}</span>
                        <div className={'saved-search-item-content'}>
                            <span className={'saved-search-item-date'}>{savedQuery.date}</span>
                            <span className={'saved-search-item-time'}>{savedQuery.time}</span>
                            <span>UTC</span>
                        </div>
                    </div>
                </HibuTooltip>
                {mayShare &&
                    <SvgIcon
                        id="icon-shared-search-shared"
                        className={`saved-search-icon`}
                        icon={Icons.SHARED_ITEM}
                        fading={!savedQuery.shared}
                        onClick={handleShare}
                        alt={getIconTitle()}
                    />
                }
                {editable &&
                    <SvgIcon
                        id="icon-shared-search-delete"
                        className="saved-search-icon"
                        icon={Icons.DELETE}
                        onClick={onDeleteClick(handleDelete)}
                        alt={t('search.saveSearch.remove')}
                    />
                }
            </div>
        </Dropdown.Item>
    );
});
